<template>
  <div>
    <drop @drop="handleDrop">
      <div v-if="uploadInProgress" class="drop-zone">
        <v-progress-circular color="primary" indeterminate :size="55" />
      </div>

      <div v-else-if="!value" class="drop-zone drop-zone--clickable" @click.stop.prevent="openFSPicker">
        <v-icon color="primary" class="upload-icon">file_upload</v-icon>
        <span class="upload-title">{{ adminText('workflowsAll.upload_logo_a564e95', 'Upload logo') }}</span>
        <span class="upload-description">
          {{ adminText('workflowsAll.jpg_and_png_formats_square_at_least_bcb9898', 'JPG and PNG formats, square, at least') }} {{ workflowLabel(minimumSizePx) }}x{{ workflowLabel(minimumSizePx) }}px.
        </span>
      </div>

      <div v-else class="drop-zone drop-zone--filled">
        <div class="logo-plate">
          <img :src="value" class="logo" :alt="adminText('workflowsAll.sponsor_logo_8a45ea1', 'Sponsor logo')">
        </div>
        <v-layout class="logo-actions" justify-center>
          <v-flex shrink class="pr-2">
            <v-btn icon class="size-32 m-0 primary" @click.stop="openFSPicker">
              <v-icon>photo_library</v-icon>
            </v-btn>
          </v-flex>
          <v-flex shrink>
            <v-btn icon class="size-32 m-0 error" @click.stop="removeLogo">
              <v-icon>delete</v-icon>
            </v-btn>
          </v-flex>
        </v-layout>
      </div>
    </drop>

    <hidden-file-input
      ref="file-input"
      accept="image/png,image/jpeg,.png,.jpg,.jpeg"
      :max-file-size-mb="maxFileSizeMb"
      @file="uploadLogo($event)"
    />
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import HiddenFileInput from '../shared/hidden-file-input'
import { getUploadOptions, uploadImage } from '@/utils'
import { UploadPaths } from '@/types'

/**
 * COR-1684: the sponsor logo, as the attendee app renders it - fitted whole into a square
 * tile on a white plate, never cropped.
 *
 * Deliberately not `logo-field`, which crops to a circle and re-encodes through a canvas:
 * that would flatten the transparency most sponsor logos rely on. The file is uploaded
 * exactly as chosen, like the venue map. SVG is excluded because React Native's <Image>
 * cannot decode it, and the app would show an empty tile.
 */
export default {
  mixins: [WorkflowDisplay],
  name: 'sponsor-logo-field',
  components: {
    HiddenFileInput,
  },
  props: {
    value: {
      type: String,
      default: null,
    },
    uploadOptions: {
      type: Object,
      default: () => ({}),
    },
    maxFileSizeMb: {
      type: String,
      default: '5',
    },
  },
  data() {
    return {
      uploadInProgress: false,
      // 56pt in the app, so anything below this is soft on a @2x screen.
      minimumSizePx: 112,
    }
  },
  methods: {
    handleDrop(data, event) {
      event.preventDefault()
      const {
        dataTransfer: { files },
      } = event
      if (files && files[0]) this.uploadLogo(files[0])
    },
    removeLogo() {
      this.$emit('input', null)
    },
    async uploadLogo(file) {
      this.uploadInProgress = true
      try {
        const uploadPath = this.uploadOptions.path || UploadPaths.UNGROUPED
        // Uploaded as-is: resizing here would rasterise away the alpha channel.
        const { file: upload, path } = await getUploadOptions(file, uploadPath, { resize: false })
        const url = await uploadImage(upload, path)
        this.$emit('input', url)
      } finally {
        this.uploadInProgress = false
      }
    },
  },
}
</script>

<style scoped>
.drop-zone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 176px;
  width: 176px;
  padding: 16px;
  border: 1px dashed #d9d9d9;
  border-radius: 16px;
  background-color: #f5f5f5;
}

.drop-zone--clickable {
  cursor: pointer;
}

.drop-zone--clickable:hover {
  background-color: rgba(2, 119, 189, 0.08);
}

.drop-zone--filled {
  position: relative;
  justify-content: flex-start;
}

.logo-plate {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 96px;
  width: 96px;
  padding: 8px;
  border-radius: 8px;
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
}

.logo {
  max-height: 100%;
  max-width: 100%;
  object-fit: contain;
}

.logo-actions {
  position: absolute;
  bottom: 16px;
  left: 0;
  right: 0;
}

.upload-icon {
  font-size: 32px;
  margin-bottom: 12px;
}

.upload-title {
  font-family: 'Raleway', sans-serif;
  font-size: 16px;
  font-weight: bold;
  line-height: 1.2;
  text-align: center;
  color: #0277bd;
  margin-bottom: 8px;
}

.upload-description {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 12px;
  line-height: 1.33;
  text-align: center;
  color: #979797;
}

.size-32 {
  width: 32px;
  height: 32px;
}

.logo-actions >>> .v-icon {
  font-size: 20px;
}
</style>
